import { useTranslation } from 'react-i18next';
import { ShippingData } from '../../models/rate.types';
import { formatCurrencyAmount, getCurrencySymbol, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';

interface ShippingDataDisplayProps {
  data: ShippingData;
}

export const ShippingDataDisplay = ({ data }: ShippingDataDisplayProps) => {
  const { t } = useTranslation('ratesModule');
  const currency = useCurrency();

  const hasWeightRanges = data.weight_ranges && data.weight_ranges.length > 0;
  const cod = data.cash_on_delivery;
  const extras = data.extra_charges;
  const hasCodFees = cod?.fixed_fee !== undefined || cod?.percentage_fee !== undefined;
  const hasExtraCharges =
    extras?.fuel_surcharge_percent !== undefined || extras?.extra_weight_charge_amount_per_kg !== undefined;
  const hasReturnPolicy = data.return_shipping_policy !== undefined;
  const hasReturnRanges = data.return_weight_ranges && data.return_weight_ranges.length > 0;

  return (
    <div className="space-y-4 text-sm">
      {hasWeightRanges && (
        <div>
          <p className="font-medium text-subtext mb-2">{t('itemData.shipping.weightRanges')}</p>
          <div className="border border-card-border rounded-lg overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-card-border/50">
                <tr>
                  <th className="px-3 py-2 text-xs font-medium text-subtext text-left">
                    {t('form.shipping.minWeight', 'Peso mínimo')}
                  </th>
                  <th className="px-3 py-2 text-xs font-medium text-subtext text-left">
                    {t('form.shipping.maxWeight', 'Peso máximo')}
                  </th>
                  <th className="px-3 py-2 text-xs font-medium text-subtext text-right">
                    {t('form.shipping.amount', { currency: getCurrencySymbol(currency) })}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-card-border">
                {data.weight_ranges!.map((range, idx) => (
                  <tr key={idx} className="hover:bg-card-border/30 transition-colors">
                    <td className="px-3 py-2 text-text">{range.min_weight.toFixed(3)} kg</td>
                    <td className="px-3 py-2 text-text">{range.max_weight.toFixed(3)} kg</td>
                    <td className="px-3 py-2 text-text text-right font-medium">
                      {formatCurrencyAmount(range.amount, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Return Shipping Policy */}
      {hasReturnPolicy && (
        <div>
          <p className="font-medium text-subtext mb-2">{t('itemData.shipping.returnShippingPolicy')}</p>
          <div className="border border-card-border rounded-lg p-3">
            <div className="text-text text-xs">
              {data.return_shipping_policy === 'same_price' && (
                <span>{t('itemData.shipping.returnPolicySamePrice')}</span>
              )}
              {data.return_shipping_policy === 'free' && <span>{t('itemData.shipping.returnPolicyFree')}</span>}
              {data.return_shipping_policy === 'custom' && <span>{t('itemData.shipping.returnPolicyCustom')}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Return Weight Ranges */}
      {hasReturnRanges && (
        <div>
          <p className="font-medium text-subtext mb-2">{t('itemData.shipping.returnWeightRanges')}</p>
          <div className="border border-card-border rounded-lg overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-card-border/50">
                <tr>
                  <th className="px-3 py-2 text-xs font-medium text-subtext text-left">
                    {t('form.shipping.minWeight', 'Peso mínimo')}
                  </th>
                  <th className="px-3 py-2 text-xs font-medium text-subtext text-left">
                    {t('form.shipping.maxWeight', 'Peso máximo')}
                  </th>
                  <th className="px-3 py-2 text-xs font-medium text-subtext text-right">
                    {t('form.shipping.amount', { currency: getCurrencySymbol(currency) })}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-card-border">
                {data.return_weight_ranges!.map((range, idx) => (
                  <tr key={idx} className="hover:bg-card-border/30 transition-colors">
                    <td className="px-3 py-2 text-text">{range.min_weight.toFixed(3)} kg</td>
                    <td className="px-3 py-2 text-text">{range.max_weight.toFixed(3)} kg</td>
                    <td className="px-3 py-2 text-text text-right font-medium">
                      {formatCurrencyAmount(range.amount, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(hasCodFees || hasExtraCharges) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {hasCodFees && (
            <div className="border border-card-border rounded-lg p-3">
              <p className="font-medium text-subtext mb-2 text-xs">{t('itemData.shipping.codFees')}</p>
              <div className="space-y-1 text-text">
                {cod?.fixed_fee !== undefined && (
                  <div className="text-xs">
                    {t('itemData.shipping.fixedFee')}:{' '}
                    <span className="font-medium">{formatCurrencyAmount(cod.fixed_fee, currency)}</span>
                  </div>
                )}
                {cod?.percentage_fee !== undefined && (
                  <div className="text-xs">
                    {t('itemData.shipping.percentageFee')}:{' '}
                    <span className="font-medium">{(cod.percentage_fee * 100).toFixed(2)}%</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {hasExtraCharges && (
            <div className="border border-card-border rounded-lg p-3">
              <p className="font-medium text-subtext mb-2 text-xs">{t('itemData.shipping.extraCharges')}</p>
              <div className="space-y-1 text-text">
                {extras?.fuel_surcharge_percent !== undefined && (
                  <div className="text-xs">
                    {t('itemData.shipping.fuelSurcharge')}:{' '}
                    <span className="font-medium">{extras.fuel_surcharge_percent}%</span>
                  </div>
                )}
                {extras?.extra_weight_charge_amount_per_kg !== undefined && (
                  <div className="text-xs">
                    {t('itemData.shipping.extraWeightCharge')}:{' '}
                    <span className="font-medium">
                      {formatCurrencyAmount(extras.extra_weight_charge_amount_per_kg, currency)}/kg
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {!hasWeightRanges && !hasCodFees && !hasExtraCharges && !hasReturnPolicy && (
        <p className="text-subtext italic text-xs">{t('itemData.noData')}</p>
      )}
    </div>
  );
};
